/**
 * Qualification before payment (spec 2026-09-26, section 6, decisions D1/D2).
 *
 * The entity type and member count come from the company record; the answers
 * come from the customer. `evaluateQualification` is the single decision the
 * API re-runs on every POST and the customer dialog mirrors, so both refuse the
 * same things for the same reasons.
 */
import {
  ECI_QUESTION_IDS,
  answersIndicateEci,
  entityKindFor,
  resolveScenario,
  type EntityKind,
  type Scenario,
} from "./scenario";
import { AMENDED_PARTNERSHIP_REFUSAL } from "./amendment";
import type { FederalTaxFilingMode } from "./pricing";
import { isTaxYearOffered, offeredTaxYearsSentence } from "./tax-years";
import { partnershipRepresentativeNeeded } from "./partnership-representative";

const ECI_FOLLOW_UP_IDS = [
  "eci.has_itin",
  "eci.itin",
  "eci.days_in_us",
  "eci.green_card",
  "eci.us_wages",
] as const;

const CCORP_QUESTION_IDS = [
  "ccorp.dividends_paid",
  "ccorp.us_source_payments_to_foreign_owner",
  "ccorp.all_owners_us_persons",
] as const;

/**
 * Asked of a C-corp once not every owner is a U.S. person: Form 5472 is filed
 * only by a 25% foreign-owned "reporting corporation" (i5472 Rev. 12-2023;
 * IRC 6038A(c)(1)), which is the only C-corp return we prepare (P3).
 */
const CCORP_FOREIGN_OWNER_ID = "ccorp.foreign_owner_25" as const;

/** Asked of every single-member LLC, after any ECI follow-ups. */
const SMLLC_QUESTION_IDS = ["smllc.owner_us_person"] as const;

const MMLLC_QUESTION_IDS = [
  "mmllc.all_partners_individuals",
  "mmllc.any_us_person_partner",
  "mmllc.us_source_passive_income",
  "mmllc.member_change_during_year",
] as const;

export const QUESTION_IDS = [
  ...ECI_QUESTION_IDS,
  ...CCORP_QUESTION_IDS,
  CCORP_FOREIGN_OWNER_ID,
  ...MMLLC_QUESTION_IDS,
  ...SMLLC_QUESTION_IDS,
  ...ECI_FOLLOW_UP_IDS,
] as const;

export type QuestionId = (typeof QUESTION_IDS)[number];

/** Days physically present in the US: the tax year and the two before it. */
export interface DaysInUs {
  current: number;
  prior: number;
  twoPrior: number;
}

type YesNoQuestionId = Exclude<QuestionId, "eci.itin" | "eci.days_in_us">;

export type QualificationAnswers = Partial<Record<YesNoQuestionId, boolean>> & {
  "eci.itin"?: string;
  "eci.days_in_us"?: DaysInUs;
};

export const REFUSAL_CODES = [
  "mmllc_has_eci",
  "ccorp_dividends_paid",
  "ccorp_us_source_payments_to_foreign_owner",
  "mmllc_partner_not_individual",
  "mmllc_us_person_partner",
  "mmllc_us_source_passive_income",
  "smllc_us_person_owner",
  "ccorp_us_person_owner",
  "ccorp_no_25_percent_foreign_owner",
  "substantial_presence_met",
  "green_card_holder",
  "missing_answer",
  "mmllc_coming_soon",
  "tax_year_not_offered",
  "mmllc_amended_return",
  "mmllc_representative_unavailable",
  "mmllc_member_change",
] as const;

export type RefusalCode = (typeof REFUSAL_CODES)[number];

export interface RefusalReason {
  code: RefusalCode;
  /** The question that caused it; absent for `mmllc_coming_soon`, `tax_year_not_offered`, `mmllc_amended_return`, `mmllc_representative_unavailable` and an owner refusal that comes from the record, not an answer. */
  question?: QuestionId;
}

function taxYearNotOfferedExplanation(now?: Date): string {
  return `${offeredTaxYearsSentence(now)} A tax professional can prepare an earlier year by hand.`;
}

/** Plain-words copy for the refusal screen ("Talk to us" is added by the UI). */
export const REFUSAL_COPY: Record<
  RefusalCode,
  { title: string; explanation: string }
> = {
  mmllc_has_eci: {
    title: "Your partnership has US business activity",
    explanation:
      "A multi-member LLC with US employees, an office, inventory, services performed in the US or US real estate owes US tax and withholding we do not prepare.",
  },
  ccorp_dividends_paid: {
    title: "Your corporation paid dividends",
    explanation:
      "Dividends to a foreign shareholder need withholding returns (Forms 1042 and 1042-S) that we do not prepare.",
  },
  ccorp_us_source_payments_to_foreign_owner: {
    title:
      "Your corporation paid its foreign owner interest, royalties or rent",
    explanation:
      "These US-source payments need withholding returns (Forms 1042 and 1042-S) that we do not prepare.",
  },
  mmllc_partner_not_individual: {
    title: "One of your partners is a company or trust",
    explanation:
      "We prepare partnership returns only when every partner is an individual.",
  },
  mmllc_us_person_partner: {
    title: "One of your partners is a US person",
    explanation:
      "We prepare partnership returns only when every partner is a foreign individual with no US citizenship, green card or Social Security number.",
  },
  mmllc_us_source_passive_income: {
    title: "Your partnership has US dividends, rent or royalties",
    explanation:
      "US dividends, rent, royalties, or interest other than from a US bank account, paid to foreign partners need withholding filings (Forms 1042 and 1042-S) we do not prepare. Interest on a US checking or savings account is fine.",
  },
  mmllc_member_change: {
    title: "A member joined, left or changed share during the year",
    explanation:
      "When members change during the year, each member's share of income depends on the dates, so the return needs a different allocation that we don't prepare yet. Write to support@nonresident.tax and we will send you a written quote to prepare it by hand.",
  },
  smllc_us_person_owner: {
    title: "Your company is owned by a US person",
    explanation:
      "A single-member LLC owned by a US citizen or US resident reports on the owner’s own Form 1040 and files no Form 5472. We prepare returns only for companies with a foreign owner.",
  },
  ccorp_us_person_owner: {
    title: "Your corporation is owned by US persons",
    explanation:
      "A corporation with no foreign owner files Form 1120 without Form 5472. We prepare returns only for companies with a foreign owner.",
  },
  ccorp_no_25_percent_foreign_owner: {
    title: "No foreign owner holds 25% of your corporation",
    explanation:
      "A US corporation files Form 5472 only when one foreign person owns at least 25% of it, directly or indirectly, by vote or by value. Without such an owner it files Form 1120 alone, which we do not prepare.",
  },
  substantial_presence_met: {
    title: "You may be a US tax resident",
    explanation:
      "Your days in the US meet the substantial presence test, so you would file as a resident (Form 1040), not as a nonresident.",
  },
  green_card_holder: {
    title: "You hold a green card",
    explanation:
      "A green card holder is a US tax resident and files Form 1040, not the nonresident return we prepare.",
  },
  missing_answer: {
    title: "A question is unanswered",
    explanation:
      "Answer every question so we can check that we can file for you.",
  },
  tax_year_not_offered: {
    title: "We don't prepare this tax year for your company",
    explanation: taxYearNotOfferedExplanation(),
  },
  // PG-22: a filed partnership return is corrected with an AAR we do not prepare (amendment.ts).
  mmllc_amended_return: AMENDED_PARTNERSHIP_REFUSAL,
  // NT-61: the return must name our partnership representative and none is configured.
  mmllc_representative_unavailable: {
    title: "We cannot take multi-member filings right now",
    explanation:
      "We cannot prepare this partnership return at the moment. Nothing has been charged. Write to support@nonresident.tax and we will tell you when we can take it.",
  },
  mmllc_coming_soon: {
    title: "We cannot take this partnership return right now",
    explanation:
      "Multi-member LLC filing is paused at the moment. Nothing has been charged. Write to support@nonresident.tax and we will tell you when we can take it, or help you find a preparer for this year's Form 1065.",
  },
};

/** A refusal's copy at `now`: the tax-year refusal names the current season (REFUSAL_COPY is the clockless copy). */
export function refusalCopyAt(
  code: RefusalCode,
  now?: Date,
): (typeof REFUSAL_COPY)[RefusalCode] {
  return code === "tax_year_not_offered"
    ? {
        ...REFUSAL_COPY.tax_year_not_offered,
        explanation: taxYearNotOfferedExplanation(now),
      }
    : REFUSAL_COPY[code];
}

/**
 * One owner (shareholder or member) as the company record holds them. The
 * `shareholders` table has no citizenship column; `citizenship` is read only
 * when a record carries one. The owner's address never counts: a nonresident
 * can give a U.S. mailing address (virtual mailbox, registered agent).
 */
export interface QualificationOwner {
  ownershipType?: string | null;
  citizenship?: string | null;
  /** The tax id and its type on file; only `partnershipRepresentativeNeeded` reads them. */
  tin?: string | null;
  tinType?: string | null;
}

function isUsCountry(value: string | null | undefined): boolean {
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\./g, "");
  return (
    normalized === "us" ||
    normalized === "usa" ||
    normalized === "united states" ||
    normalized === "united states of america"
  );
}

/**
 * The owner on file is recorded as a U.S. citizen: an individual whose
 * citizenship is the U.S. A Social Security number or a U.S. address alone
 * never counts (a nonresident can hold or give either); residency (green card,
 * days in the U.S.) is what the qualification questions ask.
 */
export function isUsPersonOwner(owner: QualificationOwner): boolean {
  const type = String(owner.ownershipType ?? "individual")
    .trim()
    .toLowerCase();
  if (type !== "individual") return false;
  return isUsCountry(owner.citizenship);
}

/** Anything other than an individual (a company, a trust) is not a foreign-individual partner. */
function isNonIndividualOwner(owner: QualificationOwner): boolean {
  return (
    String(owner.ownershipType ?? "individual")
      .trim()
      .toLowerCase() !== "individual"
  );
}

/**
 * The refusal the owners on file cause for this kind of company, or null.
 * The API gate re-runs it at checkout, so an owner edited after qualifying
 * cannot open a payment path. An MMLLC needs every partner to be a foreign
 * individual: a company or trust on file is named first, then a partner
 * recorded as a U.S. citizen (an address or Social Security number alone
 * never counts, as for the other kinds).
 */
export function ownerRefusalFor(
  kind: EntityKind,
  owners: readonly QualificationOwner[] | undefined,
): RefusalReason | null {
  if (!owners || owners.length === 0) return null;
  if (kind === "smllc" && owners.some(isUsPersonOwner)) {
    return { code: "smllc_us_person_owner" };
  }
  if (kind === "mmllc") {
    if (owners.some(isNonIndividualOwner)) {
      return { code: "mmllc_partner_not_individual" };
    }
    if (owners.some(isUsPersonOwner)) {
      return { code: "mmllc_us_person_partner" };
    }
  }
  if (kind === "ccorp" && owners.every(isUsPersonOwner)) {
    return { code: "ccorp_us_person_owner" };
  }
  return null;
}

export interface QualificationInput {
  companyType: string | null | undefined;
  memberCount: number;
  answers: QualificationAnswers;
  /**
   * The owners on file. Optional: absent or empty means nothing is known.
   * An SMLLC with an owner recorded as a U.S. citizen and a C-corp whose every
   * holder is recorded as one are refused whatever the answers say (no foreign
   * owner, no Form 5472), and so is an MMLLC with any partner on file that is
   * a company or trust or is recorded as a U.S. citizen.
   */
  owners?: QualificationOwner[];
  /**
   * Owner decision O4 (spec 2026-09-27): multi-member LLCs are accepted only
   * while this is true. Off by default; the API passes
   * `mmllcSupportedFromEnv(env.FEDERAL_TAX_MMLLC_SUPPORTED)`, which is on for
   * the local graph, staging and production (NT-17, 2026-10-22; the IRS
   * questions are answered in research/mmllc-k2k3.md). "false" is the kill switch.
   */
  mmllcSupported?: boolean;
  /**
   * The tax year being qualified. When given, a year the Worker does not build
   * for the resolved scenario (`isTaxYearOffered`, owner decision T6) is refused
   * here, before payment, instead of being sold and prepared by hand.
   */
  taxYear?: number;
  /** The clock for `taxYear`: with it the current season's year is never refused (`isTaxYearOffered`). */
  now?: Date;
  /**
   * The filing being bought. An amended multi-member LLC return is refused
   * here, before payment (`mmllc_amended_return`, PG-22).
   */
  filingMode?: FederalTaxFilingMode;
  /**
   * NT-61: whether a valid partnership representative is configured. Only an
   * explicit `false` refuses (`mmllc_representative_unavailable`), and only a
   * partnership whose return will name the representative
   * (`partnershipRepresentativeNeeded`); the browser mirror leaves it unset.
   */
  partnershipRepresentativeConfigured?: boolean;
}

/** `FEDERAL_TAX_MMLLC_SUPPORTED` → the switch: only the value "true" turns it on. */
export function mmllcSupportedFromEnv(value: unknown): boolean {
  return typeof value === "string" && value.trim().toLowerCase() === "true";
}

export type QualificationResult =
  | { eligible: true; scenario: Scenario; hasItin: boolean; usWages: boolean }
  | { eligible: false; reasons: RefusalReason[] };

/**
 * Substantial presence test (IRS Publication 519): current year + ⅓ of the
 * prior year + ⅙ of the year before that ≥ 183, AND at least 31 days in the
 * current year. Compared in sixths of a day (6c + 2p + t ≥ 1098) so the
 * fractions are exact: in floating point 100 + 199/3 + 100/6 reads
 * 182.99999999999997 and a resident at exactly 183 would pass as a
 * nonresident (audit2 Q3).
 */
export function meetsSubstantialPresence(days: DaysInUs): boolean {
  if (days.current < 31) return false;
  return 6 * days.current + 2 * days.prior + days.twoPrior >= 6 * 183;
}

export function isValidItin(value: string): boolean {
  return /^9\d{8}$/.test(value);
}

/** The questions this company is asked, given the answers so far. */
export function questionsFor(input: QualificationInput): QuestionId[] {
  const kind = entityKindFor(input.companyType, input.memberCount);
  const ids: QuestionId[] = [...ECI_QUESTION_IDS];
  if (kind === "ccorp") ids.push(...CCORP_QUESTION_IDS);
  if (
    kind === "ccorp" &&
    input.answers["ccorp.all_owners_us_persons"] === false
  ) {
    ids.push(CCORP_FOREIGN_OWNER_ID);
  }
  if (kind === "mmllc") ids.push(...MMLLC_QUESTION_IDS);
  if (kind === "smllc" && answersIndicateEci(input.answers)) {
    ids.push("eci.has_itin");
    if (input.answers["eci.has_itin"] === true) ids.push("eci.itin");
    ids.push("eci.days_in_us", "eci.green_card", "eci.us_wages");
  }
  if (kind === "smllc") ids.push(...SMLLC_QUESTION_IDS);
  return ids;
}

export function evaluateQualification(
  input: QualificationInput,
): QualificationResult {
  const { answers } = input;
  const kind = entityKindFor(input.companyType, input.memberCount);

  const missing: RefusalReason[] = questionsFor(input)
    .filter((id) => answers[id] === undefined)
    .map((question) => ({ code: "missing_answer", question }));
  if (missing.length > 0) return { eligible: false, reasons: missing };

  const reasons: RefusalReason[] = [];
  if (kind === "mmllc") {
    for (const id of ECI_QUESTION_IDS) {
      if (answers[id] === true)
        reasons.push({ code: "mmllc_has_eci", question: id });
    }
    if (answers["mmllc.all_partners_individuals"] === false) {
      reasons.push({
        code: "mmllc_partner_not_individual",
        question: "mmllc.all_partners_individuals",
      });
    }
    if (answers["mmllc.any_us_person_partner"] === true) {
      reasons.push({
        code: "mmllc_us_person_partner",
        question: "mmllc.any_us_person_partner",
      });
    }
    if (answers["mmllc.us_source_passive_income"] === true) {
      reasons.push({
        code: "mmllc_us_source_passive_income",
        question: "mmllc.us_source_passive_income",
      });
    }
    if (answers["mmllc.member_change_during_year"] === true) {
      reasons.push({
        code: "mmllc_member_change",
        question: "mmllc.member_change_during_year",
      });
    }
  }
  if (kind === "smllc" && answers["smllc.owner_us_person"] === true) {
    reasons.push({
      code: "smllc_us_person_owner",
      question: "smllc.owner_us_person",
    });
  }
  if (kind === "ccorp" && answers["ccorp.all_owners_us_persons"] === true) {
    reasons.push({
      code: "ccorp_us_person_owner",
      question: "ccorp.all_owners_us_persons",
    });
  }
  // The same refusal from the owners on file, when the answer said No.
  const ownerRefusal = ownerRefusalFor(kind, input.owners);
  if (
    ownerRefusal &&
    !reasons.some((reason) => reason.code === ownerRefusal.code)
  ) {
    reasons.push(ownerRefusal);
  }
  if (kind === "ccorp") {
    if (answers["ccorp.dividends_paid"] === true) {
      reasons.push({
        code: "ccorp_dividends_paid",
        question: "ccorp.dividends_paid",
      });
    }
    if (answers["ccorp.us_source_payments_to_foreign_owner"] === true) {
      reasons.push({
        code: "ccorp_us_source_payments_to_foreign_owner",
        question: "ccorp.us_source_payments_to_foreign_owner",
      });
    }
    if (
      answers["ccorp.all_owners_us_persons"] === false &&
      answers[CCORP_FOREIGN_OWNER_ID] === false
    ) {
      reasons.push({
        code: "ccorp_no_25_percent_foreign_owner",
        question: CCORP_FOREIGN_OWNER_ID,
      });
    }
  }

  const scenario = resolveScenario(
    input.companyType,
    input.memberCount,
    answers,
  );
  if (scenario === "smllc_eci") {
    const days = answers["eci.days_in_us"];
    if (days && meetsSubstantialPresence(days)) {
      reasons.push({
        code: "substantial_presence_met",
        question: "eci.days_in_us",
      });
    }
    if (answers["eci.green_card"] === true) {
      reasons.push({ code: "green_card_holder", question: "eci.green_card" });
    }
  }

  if (reasons.length > 0) return { eligible: false, reasons };
  // T6: a year we do not build for this scenario is not sold (and not prepared by hand).
  if (
    input.taxYear !== undefined &&
    !isTaxYearOffered(scenario, input.taxYear, input.now)
  ) {
    return { eligible: false, reasons: [{ code: "tax_year_not_offered" }] };
  }
  // PG-22: a partnership corrects a filed return with an AAR we do not prepare.
  if (kind === "mmllc" && input.filingMode === "amended") {
    return { eligible: false, reasons: [{ code: "mmllc_amended_return" }] };
  }
  // A partnership that would qualify is still refused while the switch is off.
  // A specific reason above wins, because it still applies once MMLLC opens.
  if (kind === "mmllc" && input.mmllcSupported !== true) {
    return { eligible: false, reasons: [{ code: "mmllc_coming_soon" }] };
  }
  // NT-61: a return that must name the representative is not sold while none is configured.
  if (
    kind === "mmllc" &&
    input.partnershipRepresentativeConfigured === false &&
    partnershipRepresentativeNeeded({
      kind,
      members: input.owners,
      filingMode: input.filingMode,
      taxYear: input.taxYear,
      now: input.now,
    })
  ) {
    return {
      eligible: false,
      reasons: [{ code: "mmllc_representative_unavailable" }],
    };
  }
  return {
    eligible: true,
    scenario,
    hasItin: scenario === "smllc_eci" && answers["eci.has_itin"] === true,
    usWages: scenario === "smllc_eci" && answers["eci.us_wages"] === true,
  };
}

const QUESTION_ID_SET = new Set<string>(QUESTION_IDS);

function isDayCount(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isInteger(value) &&
    value >= 0 &&
    value <= 366
  );
}

/**
 * Shape-check a request body's `answers`. Unknown ids and wrong types are
 * errors (never silently dropped); the ITIN is stored as its 9 digits.
 */
export function parseQualificationAnswers(
  raw: unknown,
):
  | { ok: true; answers: QualificationAnswers }
  | { ok: false; errors: string[] } {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return { ok: false, errors: ["answers must be an object"] };
  }
  const answers: Record<string, unknown> = {};
  const errors: string[] = [];
  for (const [id, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!QUESTION_ID_SET.has(id)) {
      errors.push(`${id} is not a qualification question`);
      continue;
    }
    if (id === "eci.itin") {
      const digits =
        typeof value === "string" ? value.replace(/[\s-]/g, "") : "";
      if (!isValidItin(digits)) {
        errors.push("eci.itin must be 9 digits starting with 9");
        continue;
      }
      answers[id] = digits;
      continue;
    }
    if (id === "eci.days_in_us") {
      const days = value as Partial<DaysInUs> | null;
      if (
        !days ||
        typeof days !== "object" ||
        !isDayCount(days.current) ||
        !isDayCount(days.prior) ||
        !isDayCount(days.twoPrior)
      ) {
        errors.push(
          "eci.days_in_us must hold whole day counts between 0 and 366 for current, prior and twoPrior",
        );
        continue;
      }
      answers[id] = {
        current: days.current,
        prior: days.prior,
        twoPrior: days.twoPrior,
      };
      continue;
    }
    if (typeof value !== "boolean") {
      errors.push(`${id} must be true or false`);
      continue;
    }
    answers[id] = value;
  }
  if (errors.length > 0) return { ok: false, errors };
  return { ok: true, answers: answers as QualificationAnswers };
}
